// -----------------------------------------------------------------------------------------------------------------------
// This files contains the logic for the Switch Time/Energy used in the data panel
// -----------------------------------------------------------------------------------------------------------------------

// Imports

// React
import * as React from 'react';
import { IconButton } from '@mui/material';
import { Clock, ZapCircle } from '@untitledui-pro/icons/line';

// Store Zustand
import { getGrid } from 'store/LoadingStore';

// Slice
import { useDataLayerSlice } from '@gui/store/hooks';

// Constants
import { RESULT_TYPES, findTimeIndex, findEnergyIndex, ResultKind } from '@gui/constants/Data';

import usePersistentStore from '@gui/store/persistent/persistentStore';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { ControllerManager } from '@gui/controller/ControllerManager';

// ----------- ResultSwitch -----------------------------------------------------------
export default function ResultSwitch() {
    const setDataLayer = usePersistentStore((s) => s.setDataLayer);
    const segments = usePersistentStore((s) => s.getActiveLayerState()?.segments);
    const data = useDataLayerSlice().data;
    const pipelineController = React.useMemo(() => ControllerManager.getInstance().getPipelineController(), []);

    const results = getGrid().getActiveLayer()?.results ?? [];
    const tIdx = findTimeIndex(results);
    const eIdx = findEnergyIndex(results);

    const activeId = data?.activeResultId ?? 0;
    const selected: ResultKind | null = activeId === tIdx ? 'TIME' : activeId === eIdx ? 'ENERGY' : null;

    const canTime = tIdx >= 0;
    const canEnergy = eIdx >= 0;
    const disabled = !canTime && !canEnergy;

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (selected === 'TIME' && canEnergy) {
            setDataLayer({ activeResultId: eIdx });
        }
        if (selected === 'ENERGY' && canTime) {
            setDataLayer({ activeResultId: tIdx });
        }

        const activeLayer = getGrid().getActiveLayer();
        if (segments?.isAggregate) {
            activeLayer.activateAggregator(activeLayer.getAggregateFrames(segments.framesList));
        }

        pipelineController.run();
    };

    const icon = selected === 'ENERGY' ? <ZapCircle className="icon-size-medium" /> : <Clock className="icon-size-medium" />;
    const label = selected === 'ENERGY' ? RESULT_TYPES.ENERGY.label : RESULT_TYPES.TIME.label;
    const color = selected === 'ENERGY' ? 'warning' : 'primary';

    if (disabled) return null;

    return (
        <CustomTooltip side="left" title={label}>
            <IconButton
                disabled={disabled}
                size="small"
                onClick={handleToggle}
                onMouseDown={(event) => event.stopPropagation()}
                color={color}
                sx={{ p: 0.25 }}
            >
                {icon}
            </IconButton>
        </CustomTooltip>
    );
}
